/**
 * Screen Wake Lock コントローラ。
 *
 * 用途:
 *   オンライン対戦 / CPU 対戦 / 観戦の間、ブラウザ／OS の自動スリープを抑止し、
 *   相手のショット待ちで画面が消える問題を防ぐ。
 *
 * 設計（GPL 非依存・自前コード）:
 *   - 標準の Screen Wake Lock API（`navigator.wakeLock.request('screen')`）を使用。
 *   - class 禁止ルールに従い、ファクトリ関数 + クロージャで状態を保持する。
 *   - タブが非表示になると WakeLockSentinel は自動 release されるため、
 *     `visibilitychange` で可視復帰時に自動再取得する。
 *   - HTTPS / localhost / タブ可視 が必須条件。非対応環境は no-op（呼び出し側はエラー扱いしない）。
 *
 * 注意:
 *   - acquire/release は idempotent（多重呼び出し安全）。
 *   - 取得失敗（権限拒否・省電力モード等）は console.warn のみで例外を投げない。
 *   - SPA 全体で 1 インスタンスを生成し、対戦開始/終了で acquire/release を呼ぶ想定。
 */

export interface WakeLockController {
  /** Wake Lock を取得（既に取得済みなら何もしない）。失敗しても reject しない。 */
  acquire(): Promise<void>;
  /** Wake Lock を解放（取得していなければ何もしない）。 */
  release(): Promise<void>;
  /** インスタンスを破棄（イベントリスナー解除＋強制解放）。再利用不可。 */
  dispose(): void;
  /** 現在 Wake Lock が有効かどうか。デバッグ・テスト用途。 */
  isActive(): boolean;
}

const isSupported = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  // `wakeLock` プロパティが lib.dom 型に無い古い TS 環境でも安全に検出。
  const nav = navigator as Navigator & { wakeLock?: WakeLock };
  return typeof nav.wakeLock !== 'undefined' && nav.wakeLock !== null;
};

const isPageVisible = (): boolean => {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible';
};

/**
 * Screen Wake Lock コントローラを生成する。
 * 呼び出し側は対戦開始時に `acquire()`、終了時に `release()` を呼ぶだけでよい。
 */
export const createWakeLockController = (): WakeLockController => {
  let sentinel: WakeLockSentinel | null = null;
  // ユーザー意図（acquire 要求中か否か）。
  // sentinel が null でも desired=true なら可視復帰時に再取得する。
  let desired = false;
  let disposed = false;

  const requestLock = async (): Promise<void> => {
    if (disposed) return;
    if (!isSupported()) return;
    if (sentinel !== null && !sentinel.released) return; // 既に保持中
    if (!isPageVisible()) return; // 非表示中は取得不可（visibilitychange で再取得）

    try {
      const s = await navigator.wakeLock.request('screen');
      // request 中に dispose / release が呼ばれた場合は即解放。
      if (disposed || !desired) {
        await s.release().catch((): void => {});
        return;
      }
      sentinel = s;
      // OS / ブラウザ都合（タブ非表示・低バッテリー等）で release された場合の追従。
      // 再取得は visibilitychange で行うため、ここでは sentinel をクリアするだけ。
      s.addEventListener('release', (): void => {
        if (sentinel === s) sentinel = null;
      });
    } catch (err) {
      // 権限拒否・省電力モード・ユーザー操作なしでの取得試行等で失敗し得る。
      // 致命的ではないため warn のみで継続する。
      console.warn('[wake-lock] acquire failed:', err);
    }
  };

  const handleVisibilityChange = (): void => {
    if (disposed || !desired) return;
    if (isPageVisible()) {
      // 非表示中に自動 release された可能性があるため再取得を試みる。
      void requestLock();
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  const acquire = async (): Promise<void> => {
    if (disposed) return;
    desired = true;
    await requestLock();
  };

  const release = async (): Promise<void> => {
    desired = false;
    const current = sentinel;
    sentinel = null;
    if (current !== null && !current.released) {
      try {
        await current.release();
      } catch (err) {
        console.warn('[wake-lock] release failed:', err);
      }
    }
  };

  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    desired = false;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    const current = sentinel;
    sentinel = null;
    if (current !== null && !current.released) {
      current.release().catch((): void => {});
    }
  };

  const isActive = (): boolean => sentinel !== null && !sentinel.released;

  return { acquire, release, dispose, isActive };
};
